// =============================================================================
// features/sales/campaigns/operate/OperateContent.tsx — Orquestra o resumo
// da campanha, o gate de "operável agora" e a máquina de estados da fila
// (doc 26 §6.8). Montado só depois que `OperatePage` libera permissão+flag.
// =============================================================================

import * as React from 'react';

import { checkCampaignOperable } from './campaignStatus';
import { LeadContextCard } from './components/LeadContextCard';
import { LeadSidePanel } from './components/LeadSidePanel';
import { OperateHeader } from './components/OperateHeader';
import {
  OperateErrorState,
  CampaignNotOperableState,
  EmptyQueueState,
  OperateSkeleton,
} from './components/OperateStates';
import { OperationFooter } from './components/OperationFooter';
import { useCampaignSummary } from './useCampaignSummary';
import { useQueueSession } from './useQueueSession';

export interface OperateContentProps {
  campaignId: string;
}

export function OperateContent({ campaignId }: OperateContentProps): React.JSX.Element {
  const {
    campaign,
    isLoading: campaignLoading,
    isError: campaignError,
    refetch: refetchCampaign,
  } = useCampaignSummary(campaignId);

  const operableCheck = campaign ? checkCampaignOperable(campaign.status) : null;
  const sessionActive = operableCheck?.operable === true;

  const session = useQueueSession(campaignId, sessionActive);

  if (campaignLoading) {
    return <OperateSkeleton />;
  }

  if (campaignError || !campaign) {
    return (
      <OperateErrorState
        message="Não foi possível carregar os dados desta campanha. Verifique sua conexão e tente novamente."
        onRetry={() => refetchCampaign()}
      />
    );
  }

  if (operableCheck && !operableCheck.operable) {
    return (
      <div className="flex flex-col gap-6">
        <OperateHeader campaign={campaign} claimExpiresAt={null} leaseLost={false} />
        <CampaignNotOperableState check={operableCheck} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-4">
      <OperateHeader
        campaign={campaign}
        claimExpiresAt={session.member?.claimExpiresAt ?? null}
        leaseLost={session.leaseLost}
      />

      {session.heartbeatWarning && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-sm px-4 py-2.5"
          style={{ background: 'var(--warning-bg)' }}
        >
          <span className="font-sans text-xs font-semibold text-warning">
            Sem confirmação de conexão com o servidor no momento — tentando renovar sua reserva
            automaticamente. Nada do que você preencher será perdido.
          </span>
        </div>
      )}

      {session.phase === 'claiming' && <OperateSkeleton />}

      {session.phase === 'error' && (
        <OperateErrorState
          message={session.errorMessage ?? 'Erro inesperado ao buscar o próximo cliente.'}
          onRetry={() => void session.claimNext()}
        />
      )}

      {session.phase === 'empty' && <EmptyQueueState onRetry={() => void session.claimNext()} />}

      {session.phase === 'active' && session.member && session.lead && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
            <LeadContextCard lead={session.lead} member={session.member} />
            <LeadSidePanel leadId={session.lead.id} />
          </div>

          <OperationFooter
            campaignId={campaignId}
            member={session.member}
            lead={session.lead}
            resolved={session.resolved}
            resolvedMember={session.resolvedMember}
            leaseLost={session.leaseLost}
            isReleasing={session.isReleasing}
            isResolving={session.isResolving}
            onReleaseAndAdvance={session.releaseAndAdvance}
            onResolveAndAdvance={session.resolveAndAdvance}
            onNext={() => void session.claimNext()}
          />
        </>
      )}
    </div>
  );
}
